import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { buildTenantPath } from '../../utils/tenantSlug';
import { readFinancialSetupOverview, type FinancialSetupOverview as SetupOverview } from '../../services/FinancialSetupService';
import { buildFinancialSetupCards, type FinancialSetupCardStatus } from '../../services/financialSetupCards';

const styles: Record<FinancialSetupCardStatus, { card: string; badge: string }> = {
  blocked: { card: 'border-red-300 bg-red-50', badge: 'bg-red-100 text-red-800' },
  attention: { card: 'border-amber-300 bg-amber-50', badge: 'bg-amber-100 text-amber-800' },
  ready: { card: 'border-green-300 bg-green-50', badge: 'bg-green-100 text-green-800' },
};

export default function FinancialSetupOverview({ clientId, slug }: { clientId: string; slug?: string }) {
  const [data, setData] = useState<SetupOverview | null>(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setData(null); setError('');
    readFinancialSetupOverview(clientId, controller.signal)
      .then(result => { if (!controller.signal.aborted) setData(result); })
      .catch(cause => { if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : 'Não foi possível carregar a preparação financeira.'); });
    return () => controller.abort();
  }, [clientId, attempt]);

  const cards = useMemo(() => data ? buildFinancialSetupCards(data) : [], [data]);
  const completed = cards.filter(card => card.status === 'ready').length;
  const blockers = cards.filter(card => card.required && card.status === 'blocked').length;

  return <section className="space-y-5" aria-labelledby="financial-setup-title">
    <div className={`rounded-lg border p-5 ${blockers ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="financial-setup-title" className="text-lg font-semibold text-gray-900">Preparação do financeiro</h2>
          <p className="mt-1 text-sm text-gray-700">Conclua os cadastros necessários para operar recebimentos, pagamentos, contratos e repasses.</p>
        </div>
        {data && <span className="rounded-full bg-white px-3 py-1 text-sm font-semibold text-gray-800 shadow-sm">{completed} de 8 etapas configuradas</span>}
      </div>
    </div>

    {error && <div role="alert" className="rounded-lg border border-red-200 bg-white p-4">
      <p className="text-sm text-red-800">{error}</p>
      <button type="button" className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:underline" onClick={() => setAttempt(value => value + 1)}><RefreshCw className="h-4 w-4" />Tentar novamente</button>
    </div>}
    {!data && !error && <div role="status" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 8 }, (_, index) => <div key={index} className="h-40 animate-pulse rounded-lg border bg-gray-100" />)}</div>}

    {data && <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{cards.map(card => {
      const style = styles[card.status];
      return <Link key={card.id} to={buildTenantPath(`/admin/${card.route}`, slug)} className={`flex min-h-44 flex-col rounded-lg border p-4 transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${style.card}`}>
        <div className="flex items-start justify-between gap-2">
          <h3 className="min-w-0 text-gray-900">{card.title}: <span className="font-bold">{card.metric}</span></h3>
          {card.status === 'ready' ? <CheckCircle2 className="h-5 w-5 shrink-0 text-green-700" /> : <AlertTriangle className={`h-5 w-5 shrink-0 ${card.status === 'blocked' ? 'text-red-700' : 'text-amber-700'}`} />}
        </div>
        <p className="mt-2 flex-1 text-sm text-gray-700">{card.description}</p>
        <div className="mt-3 flex items-center justify-between gap-2"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${style.badge}`}>{card.statusLabel}</span><span className="text-sm font-semibold text-blue-800">{card.status === 'ready' ? 'Revisar' : 'Configurar'} →</span></div>
      </Link>;
    })}</div>}
  </section>;
}
